import { useEffect, useRef, useMemo, useState } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import { useForm, UseFormHandleSubmit } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import {
  IconSelected,
  IconBlock,
  IconLoader,
  Table,
  useQueryState,
  useScroll,
  formatDate,
  usePrevious,
  Filters,
  useGetList,
  PermissionEnum,
  PermissionDenied,
  SchemaFilterType,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { selectList } from './store/slices/list';
import { clearBlacklistState } from './store/slices/item';
import { blacklistListColumns, blacklistListSchema } from './variables/list';
import { Blacklist } from '../types';
import { useLazyGetBlacklistsQuery } from './store/services';

import cn from './blacklist.module.css';

const formatBlacklistList = (list: Blacklist[]): Blacklist[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {
      active: (
        <div
          className={`${cn.blacklistStatus} ${
            !result.active ? cn.blacklistRowDisabled : ''
          }`}
        >
          {result.active ? (
            <IconSelected
              className={`${cn.blacklistStatusIcon} ${cn.blacklistActiveIcon}`}
            />
          ) : (
            <IconBlock
              className={`${cn.blacklistStatusIcon} ${cn.blacklistDisabledIcon}`}
            />
          )}
        </div>
      ),
    };
    render.createdAt = (
      <div
        className={`${!result.active ? cn.blacklistRowDisabled : ''} ${
          cn.rowWithPadding
        }`}
      >
        {formatDate(item.createdAt, true)}
      </div>
    );
    render.successfulSyncLastDate = (
      <div
        className={`${!result.active ? cn.blacklistRowDisabled : ''} ${
          cn.rowWithPadding
        }`}
      >
        {item.successfulSyncLastDate
          ? formatDate(item.successfulSyncLastDate, true)
          : '—'}
      </div>
    );
    render.updatedAt = (
      <div
        className={`${!result.active ? cn.blacklistRowDisabled : ''} ${
          cn.rowWithPadding
        }`}
      >
        {formatDate(item.updatedAt, true)}
      </div>
    );
    render.lastTrySyncDate = (
      <div
        className={`${!result.active ? cn.blacklistRowDisabled : ''} ${
          cn.rowWithPadding
        }`}
      >
        {item.lastTrySyncDate ? formatDate(item.lastTrySyncDate, true) : '—'}
      </div>
    );
    render.source = (
      <div
        className={`${!result.active ? cn.blacklistRowDisabled : ''} ${
          cn.blacklistSource
        }`}
      >
        {item.source || '—'}
      </div>
    );

    render.name = (
      <div
        className={`${!result.active ? cn.blacklistRowDisabled : ''} ${
          cn.rowWithPadding
        }`}
      >
        {item.name}
      </div>
    );

    result.render = render;

    return result;
  });

  return nextList;
};

const BlacklistList = () => {
  const { handleSubmit, reset } = useForm();
  const { active } = useOutletContext<{ active: boolean }>();
  const [takeCount, setTakeCount] = useState(0);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    blacklist: {
      list: { values, selectedLists, pagination, hideInactive, errorsList },
      item: { blacklist },
    },
    amlDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const { sort, onSortChange, filters, onFilterChange } =
    useQueryState(blacklistListSchema);

  const prevFilters = usePrevious(filters);
  const prevHideInactive = usePrevious(hideInactive);

  const [getBlacklists, { isLoading, isFetching }] =
    useLazyGetBlacklistsQuery();

  const { getListAction, setPage, onSort, onFilter, errorList } =
    useGetList<Blacklist>({
      getList: getBlacklists,
      onFilterChange,
      onSortChange,
      schema: blacklistListSchema,
    });

  const formattedData = useMemo(() => {
    const data = formatBlacklistList(values as Blacklist[]);

    return data;
  }, [values, sort]);

  const onRowsSelect = (rows: string[]) => {
    if (!isEqual(selectedLists, rows)) {
      dispatch(selectList(rows));
    }
  };

  useScroll({ element: scrollRef, requestInProgress: isFetching || isLoading });

  useEffect(() => {
    reset(filters);
  }, [filters, reset]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 45;

        setTakeCount(Math.ceil(take + 3));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (
      takeCount !== 0 &&
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !values &&
      Object.keys(sort).length &&
      takeCount &&
      takeCount !== 0
    ) {
      getListAction({
        filters: {
          ...filters,
          active: { value: ['true'], type: SchemaFilterType.STRING },
          take: {
            value: [takeCount.toString()],
            type: SchemaFilterType.STRING,
          },
          sortDir: { value: ['desc'], type: SchemaFilterType.STRING },
          sortProp: { value: ['createdAt'], type: SchemaFilterType.STRING },
        },
      });
    }
  }, [filters, prevFilters, values, takeCount]);

  useEffect(() => {
    if (blacklist) {
      dispatch(clearBlacklistState());
    }
  }, [blacklist]);

  useEffect(() => {
    if (prevHideInactive !== null && hideInactive !== prevHideInactive) {
      let nextFilters: Filters = {
        ...filters,
        take: { value: [takeCount.toString()], type: SchemaFilterType.STRING },
      };

      if (hideInactive) {
        nextFilters = {
          ...nextFilters,
          active: { value: [`${hideInactive}`], type: SchemaFilterType.STRING },
        };
      }
      getListAction({ filters: nextFilters, sort });
    }
  }, [hideInactive, sort, filters, takeCount, prevHideInactive]);

  useEffect(() => {
    let title = 'Block list';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content: React.JSX.Element;

  if (isLoading || takeCount === 0) {
    content = (
      <div className={cn.blacklistLoader}>
        <IconLoader />
      </div>
    );
  } else if (
    !isLoading &&
    activePermissions?.includes(PermissionEnum.BLACKLISTS_LIST)
  ) {
    content = (
      <Table
        columns={blacklistListColumns}
        data={formattedData || []}
        onSort={(name) => onSort(name)}
        filters={filters}
        sort={sort}
        columnsLabels={{}}
        error={errorsList || errorList}
        onFilterChange={(filterValues) =>
          onFilter({
            ...filterValues,
            take: {
              value: [takeCount.toString()],
              type: SchemaFilterType.STRING,
            },
          })
        }
        handleSubmit={handleSubmit as UseFormHandleSubmit<Filters>}
        isLoading={isFetching || isLoading || !takeCount}
        onRowsSelect={
          activePermissions?.includes(PermissionEnum.BLACKLISTS_DELETE_ACTION)
            ? onRowsSelect
            : undefined
        }
        take={takeCount.toString()}
        setPage={(data) =>
          setPage({
            ...data,
            filters: {
              ...filters,
              take: {
                value: [takeCount.toString()],
                type: SchemaFilterType.STRING,
              },
            },
          })
        }
        pagination={pagination}
        clickable
      />
    );
  } else {
    content = <PermissionDenied />;
  }

  return (
    <>
      <div className={cn.blacklistTableWrapper} ref={scrollRef}>
        {content}
      </div>
      <Outlet context={{ active }} />
    </>
  );
};

export default BlacklistList;
